import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import PlaceOrderButton from 'components/FrictionlessCheckout/PlaceOrderButton/PlaceOrderButton';
import OrderActions from 'actions/OrderActions';
import Actions from 'Actions';
import orderUtils from 'utils/Order';
import { orderSelector } from 'selectors/order/orderSelector';
import { userSelector } from 'selectors/user/userSelector';
import paymentOptionsSelector from 'selectors/order/paymentOptionsSelector';
import CheckoutActions from 'actions/CheckoutActions';
import anaUtils from 'analytics/utils';
import errorsSelector from 'selectors/errors/errorsSelector';
import ErrorConstants from 'utils/ErrorConstants';
import klarnaSelector from 'selectors/klarna/klarnaSelector';
import afterpaySelector from 'selectors/afterpay/afterpaySelector';
import pazeSelector from 'selectors/paze/pazeSelector';
import venmoSelector from 'selectors/venmo/venmoSelector';
import paypalSelector from 'selectors/paypal/paypalSelector';
import googlePaySelector from 'selectors/googlePay/googlePaySelector';
import KlarnaActions from 'actions/KlarnaActions';
import AfterpayActions from 'actions/AfterpayActions';
import PazeActions from 'actions/PazeActions';
import VenmoActions from 'actions/VenmoActions';
import { swapPaypalToCreditCard } from 'services/Checkout';
import PlaceOrderButtonEN from 'components/FrictionlessCheckout/PlaceOrderButton/locales/PlaceOrderButton_en_US';
import PlaceOrderButtonFR from 'components/FrictionlessCheckout/PlaceOrderButton/locales/PlaceOrderButton_fr_CA';
import FrictionlessUtils from 'utils/FrictionlessCheckout';

const { wrapHOC } = FrameworkUtils;
const { showPlaceOrderTermsModal } = CheckoutActions;
const { getTextFromResource, getLocalisedDataFromResource } = LanguageLocaleUtils;

const getText = getLocalisedDataFromResource(PlaceOrderButtonEN, PlaceOrderButtonFR);

const fieldErrorSelector = createSelector(errorsSelector, errors => errors.FIELD);

const textResources = createStructuredSelector({
    denialMessage: getTextFromResource(getText, 'denialMessage'),
    placeOrder: getTextFromResource(getText, 'placeOrder'),
    items: getTextFromResource(getText, 'items'),
    item: getTextFromResource(getText, 'item'),
    orderTotal: getTextFromResource(getText, 'orderTotal'),
    placeFreePayment: getTextFromResource(getText, 'placeFreePayment'),
    withText: getTextFromResource(getText, 'withText'),
    forProcessingText: getTextFromResource(getText, 'forProcessingText'),
    placeOrderFor: getTextFromResource(getText, 'placeOrderFor'),
    processingOrder: getTextFromResource(getText, 'processingOrder'),
    buttonDisabled: getTextFromResource(getText, 'buttonDisabled')
});

const functions = {
    showSDUAgreementModal: Actions.showSDUAgreementModal,
    showGiftAddressWarningModal: Actions.showGiftAddressWarningModal,
    showPlaceOrderTermsModal: showPlaceOrderTermsModal,
    setCheckoutSectionErrors: OrderActions.setCheckoutSectionErrors,
    togglePlaceOrderDisabled: OrderActions.togglePlaceOrderDisabled,
    updateOrder: OrderActions.updateOrder,
    clearPendingCreditCard: OrderActions.clearPendingCreditCard,
    refreshCheckout: CheckoutActions.refreshCheckout,
    placeKlarnaOrder: KlarnaActions.placeOrder,
    placeAfterpayOrder: AfterpayActions.placeOrder,
    placePazeOrder: PazeActions.placeOrder,
    placeVenmoOrder: VenmoActions.placeOrder,
    showConfirmCVVModal: Actions.showConfirmCVVModal,
    swapPaypalToCreditCard
};

const connectedPlaceOrderButton = connect(
    createSelector(
        textResources,
        orderSelector,
        userSelector,
        paymentOptionsSelector,
        fieldErrorSelector,
        klarnaSelector,
        afterpaySelector,
        pazeSelector,
        venmoSelector,
        paypalSelector,
        googlePaySelector,
        (texts, order, user, paymentOptions, fieldErrors, klarna, afterpay, paze, venmo, paypal, googlePay) => {
            const { orderDetails } = order;
            const isAutoReplenishBasket = orderUtils.hasAutoReplenishItems(orderDetails) && Sephora.configurationSettings.isAutoReplenishmentEnabled;
            const defaultPayment = paymentOptions?.creditCards?.find(creditCard => creditCard?.isDefault);
            const isDefaultPaymentBeingUsed =
                !!defaultPayment &&
                orderDetails?.paymentGroups?.paymentGroupsEntries?.some(
                    payment => payment?.paymentGroup?.creditCardId === defaultPayment?.creditCardId
                );
            const defaultPaymentMethodName = anaUtils.getPaymentCardType(defaultPayment?.cardType)?.toLowerCase();

            return {
                ...texts,
                getText,
                isAutoReplenishBasket,
                acceptAutoReplenish: order.acceptAutoReplenishTerms,
                orderQuantity: orderDetails.items.itemCount,
                orderTotal: orderDetails.priceInfo.orderTotal,
                creditCardAmount: orderDetails.priceInfo.creditCardAmount,
                isPaypalPaymentEnabled: orderDetails.header.isPaypalPaymentEnabled,
                isAddressMisMatch: orderDetails.header.isAddressMisMatch,
                digitalGiftMsg: orderDetails.digitalGiftMsg,
                existingDefaultPayment: order.paymentOptions.defaultPayment,
                isApplePayFlow: order.isApplePayFlow,
                defaultPaymentName: user.selectedAsDefaultPaymentName,
                originOfOrder: 'web',
                orderDetails,
                defaultPaymentMethodName,
                isDefaultPaymentBeingUsed,
                isCVVError: !!fieldErrors[ErrorConstants.ERROR_CODES.CREDIT_CARD_SECURITY_CODE],
                isKlarnaSelected: klarna?.isSelected,
                isKlarnaReady: klarna?.isReady,
                isAfterpaySelected: afterpay?.isSelected,
                isAfterpayReady: afterpay?.isReady,
                isPazeSelected: paze?.isSelected,
                isPazeReady: paze?.isReady,
                isVenmoSelected: venmo?.isSelected,
                isVenmoReady: venmo?.isReady,
                isPaypalSelected: paypal?.isSelected,
                isPaypalReady: paypal?.isReady,
                isGooglePaySelected: googlePay?.isSelected,
                isGooglePayReady: googlePay?.isReady,
                placeOrderPreHook: order.placeOrderPreHook,
                addressNeedsToBeConfirmed: order.addressNeedsToBeConfirmed,
                shippingMethodNeedsToBeConfirmed: order.shippingMethodNeedsToBeConfirmed,
                giftCardAddressNeedsToBeConfirmed: order.giftCardAddressNeedsToBeConfirmed,
                sectionThatNeedsToBeConfirmed: FrictionlessUtils.sectionThatNeedsToBeConfirmed(order),
                pendingCreditCard: order.pendingCreditCard,
                sduAcceptTerms: order.acceptSDUTerms
            };
        }
    ),
    functions
);

const withPlaceOrderButtonProps = wrapHOC(connectedPlaceOrderButton);

export default withPlaceOrderButtonProps(PlaceOrderButton);
